import moment from 'moment-timezone';
import { isAppointmentHost } from 'Helpers/appointmentHost';

export const SNAP_MINUTES = 15;
export const MINUTES_PER_DAY = 1440;
export const DRAG_THRESHOLD_PX = 8;
// The library renders a block 1 minute short and gives it height: auto below 30px, which leaves no bottom edge to grab.
export const MIN_RESIZABLE_MINUTES = 31;

export type Span = { date: Date; endDate: Date };

export type HeldSpans = Record<number, Span>;

export type CalendarEvent = {
	id: number;
	isEvent?: boolean;
	isEditing?: boolean;
	calendarItemType?: string;
	projectPlanStatus?: unknown;
};

export type CalendarSelf = { id: number; administrator?: boolean };

export type MovableAppointment = {
	id: number;
	date: Date | string;
	endDate?: Date | string | null;
	time?: string | null;
	outcome?: string;
	preliminary?: boolean;
	userEditable?: boolean;
	isEvent?: boolean;
	isEditing?: boolean;
	calendarItemType?: string;
	projectPlanStatus?: unknown;
	isExternalHost?: boolean;
	regBy?: { id: number } | null;
};

function inZone(value: Date | string | number, timezone?: string) {
	return timezone ? moment.tz(value, timezone) : moment(value);
}

function onDay(day: Date, minutes: number, timezone?: string): Date {
	const startOfDay = inZone(day, timezone).startOf('day');
	if (minutes >= MINUTES_PER_DAY) {
		return startOfDay.add(1, 'day').toDate();
	}
	return startOfDay
		.hours(Math.floor(minutes / 60))
		.minutes(minutes % 60)
		.toDate();
}

export function isMovable(appointment: MovableAppointment, self: CalendarSelf, timezone?: string): boolean {
	if (
		appointment.isEvent ||
		appointment.isEditing ||
		appointment.calendarItemType ||
		appointment.projectPlanStatus ||
		appointment.userEditable === false ||
		appointment.outcome !== 'planned' ||
		appointment.preliminary ||
		appointment.time === null ||
		appointment.time === undefined ||
		!appointment.endDate
	) {
		return false;
	}

	const start = inZone(appointment.date, timezone);
	const end = inZone(appointment.endDate, timezone);
	if (!end.isAfter(start) || !start.isSame(end.clone().subtract(1, 'ms'), 'day')) {
		return false;
	}

	return !!self.administrator || isAppointmentHost(appointment, self.id);
}

export function snap(minutes: number): number {
	return Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES;
}

export function wallMinutes(date: Date, timezone?: string): number {
	const m = inZone(date, timezone);
	return m.hours() * 60 + m.minutes();
}

export function movedSpan(span: Span, day: Date, startMinutes: number, timezone?: string): Span {
	const duration = span.endDate.getTime() - span.date.getTime();
	const latestStart = Math.max(0, MINUTES_PER_DAY - Math.round(duration / 60_000));
	const date = onDay(day, Math.min(Math.max(snap(startMinutes), 0), latestStart), timezone);
	return { date, endDate: new Date(date.getTime() + duration) };
}

export function resizedSpan(span: Span, endMinutes: number, timezone?: string): Span {
	const earliestEnd = wallMinutes(span.date, timezone) + SNAP_MINUTES;
	return {
		date: span.date,
		endDate: onDay(span.date, Math.min(Math.max(snap(endMinutes), earliestEnd), MINUTES_PER_DAY), timezone)
	};
}

export function columnAt(columns: { right: number }[], clientX: number): number {
	const index = columns.findIndex(column => clientX < column.right);
	return index === -1 ? columns.length - 1 : index;
}

export function pointerMinutes(clientY: number, gridTop: number, pxPerMinute: number, dayStartHour: number): number {
	return dayStartHour * 60 + (clientY - gridTop) / pxPerMinute;
}

export function getViewDays(view: string, viewDate: Date, timezone?: string): Date[] {
	if (view === 'day') {
		return [viewDate];
	}
	const count = view === 'week' ? 7 : 5;
	return Array.from({ length: count }, (_, index) => inZone(viewDate, timezone).add(index, 'days').toDate());
}

export function withDragSpans<T extends { id: number }>(
	appointments: T[],
	livePreview: ({ id: number } & Span) | null,
	held: HeldSpans
): (T | (T & Span & { isDragging: boolean }))[] {
	if (!livePreview && !Object.keys(held).length) {
		return appointments;
	}
	return appointments.map(appointment => {
		const isDragging = livePreview?.id === appointment.id;
		const span = isDragging ? livePreview : held[appointment.id];
		return span ? { ...appointment, date: span.date, endDate: span.endDate, isDragging } : appointment;
	});
}

export function findAppointmentForEvent<T extends { id: number }>(event: CalendarEvent, appointments: T[]): T | null {
	if (event.isEvent || event.isEditing || event.calendarItemType || event.projectPlanStatus) {
		return null;
	}
	return appointments.find(({ id }) => id === event.id) ?? null;
}

export function isResizable(appointment: { date: Date | string; endDate?: Date | string | null }): boolean {
	return (
		!!appointment.endDate &&
		moment(appointment.endDate).diff(moment(appointment.date), 'minutes', true) >= MIN_RESIZABLE_MINUTES
	);
}
